import { useState, useEffect } from "react";
import { Button, Input, Card, CardHeader, CardTitle, CardContent } from "@repo/o37-ui";
import { supabase } from "../supabaseClient";
import MFASetupFlow from "../components/MFASetupFlow";

type UserProfile = {
  id: string;
  email: string;
  name: string;
  factors?: any[];
};

export default function ProfileSettings() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Password Change State
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // MFA State
  const [refreshMfa, setRefreshMfa] = useState(0);

  useEffect(() => {
    loadUser();
  }, [refreshMfa]);

  const loadUser = async () => {
    try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        
        if (!authUser) {
            setLoading(false);
            return;
        }

        // Try to get name from metadata or profiles table if available
        let name = authUser.user_metadata?.name || authUser.email?.split('@')[0] || "User";

        setUser({
            id: authUser.id,
            email: authUser.email || "",
            name: name,
            factors: authUser.factors || []
        });
    } catch (e) {
      console.error("Failed to load user profile", e);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (newPassword.length < 6) {
        setPasswordError("Password must be at least 6 characters");
        return;
    }

    if (newPassword !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return;
    }

    setIsSaving(true);
    try {
        const { error } = await supabase.auth.updateUser({
            password: newPassword
        });

        if (error) throw error;

        setPasswordSuccess("Password updated successfully");
        setNewPassword("");
        setConfirmPassword("");
    } catch (e: any) {
        setPasswordError(e.message || "Failed to update password");
    } finally {
        setIsSaving(false);
    }
  };

  const handleDisableMFA = async (factorId: string) => {
    if (!confirm("Are you sure you want to disable Two-Factor Authentication?")) return;
    
    try {
        const { error } = await supabase.auth.mfa.unenroll({ factorId });
        if (error) throw error;
        setRefreshMfa(k => k + 1);
        
        // Also update local tracking in profiles table if needed (via backend or just rely on auth state)
        // Since we track mfa_enabled_at in profiles via API routes usually, we might want to clear it
        // Ideally we have an endpoint for this, but for now we just unenroll from Auth
    } catch (e: any) {
        alert(e.message || "Failed to disable MFA");
    }
  };

  const handleMFAComplete = async () => {
      setRefreshMfa(k => k + 1);
  };

  if (loading) {
    return <div className="p-8">Loading profile...</div>;
  }

  if (!user) {
    return <div className="p-8">User not found. Please log in again.</div>;
  }

  const mfaEnabled = user.factors && user.factors.length > 0 && user.factors.some(f => f.status === 'verified');

  return (
    <div className="min-h-full pb-12">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold mb-2">Profile Settings</h1>
        <p className="text-neutral-500">Manage your account settings and preferences.</p>
      </div>

      <div className="max-w-2xl space-y-8">
        {/* Basic Info */}
        <Card>
            <CardHeader>
                <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1.5 text-neutral-700">Full Name</label>
                        <Input 
                            value={user.name} 
                            disabled 
                            className="bg-neutral-50 text-neutral-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1.5 text-neutral-700">Email Address</label>
                        <Input 
                            value={user.email} 
                            disabled 
                            className="bg-neutral-50 text-neutral-500"
                        />
                    </div>
                </div>
            </CardContent>
        </Card>

        {/* Two-Factor Authentication */}
        <Card>
            <CardHeader>
                <CardTitle>Two-Factor Authentication (2FA)</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
                {mfaEnabled ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                                <span className="text-xl">✓</span>
                            </div>
                            <div>
                                <h3 className="font-medium text-green-900">2FA is Enabled</h3>
                                <p className="text-sm text-green-700">Your account is secured with an authenticator app.</p>
                            </div>
                        </div>
                        <Button 
                            variant="danger-outline" 
                            onClick={() => handleDisableMFA(user.factors![0].id)} 
                            text="Disable" 
                        />
                    </div>
                ) : (
                    <MFASetupFlow onComplete={handleMFAComplete} />
                )}
            </CardContent>
        </Card>

        {/* Password Change */}
        <Card>
            <CardHeader>
                <CardTitle>Security</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
                <form onSubmit={handlePasswordChange}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-neutral-700">New Password</label>
                            <Input 
                                type="password"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-neutral-700">Confirm New Password</label>
                            <Input 
                                type="password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                required
                            />
                        </div>
                    </div>

                    {passwordError && (
                        <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded border border-red-100">
                            {passwordError}
                        </div>
                    )}

                    {passwordSuccess && (
                        <div className="mt-4 p-3 bg-green-50 text-green-600 text-sm rounded border border-green-100">
                            {passwordSuccess}
                        </div>
                    )}

                    <div className="mt-6 flex justify-end">
                        <Button 
                            type="submit" 
                            text={isSaving ? "Saving..." : "Update Password"} 
                            disabled={isSaving}
                        />
                    </div>
                </form>
            </CardContent>
        </Card>
      </div>
    </div>
  );
}
